import { EnvType, TCoord_system } from '@metadata';
import { Collection, Map as OlMap, Overlay, View } from 'ol';
import { defaults as defaultControls } from 'ol/control';
import { Coordinate } from 'ol/coordinate';
import Feature from 'ol/Feature';
import GeoJSON, { GeoJSONFeature } from 'ol/format/GeoJSON';
import { SimpleGeometry } from 'ol/geom';
import { Draw } from 'ol/interaction';
import { Layer, Tile as TileLayer, Vector as VectorLayer } from 'ol/layer';
import 'ol/ol.css';
import { get as getProjection } from 'ol/proj';
import { toSize } from "ol/size";
import { Vector as VectorSource, XYZ } from 'ol/source';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import React from 'react';
import { SetterOrUpdater } from 'recoil';
import { MapConstant } from '../constant';
import { ngiiTileLoadFunction, ngiiTileUrlFunction } from './ngii';

import { EventsKey } from 'ol/events';
import BaseLayer from 'ol/layer/Base';
import LayerGroup from 'ol/layer/Group';
import VectorImageLayer from 'ol/layer/VectorImage';
import { get } from "ol/proj";
import { register } from 'ol/proj/proj4';
import { getLength } from 'ol/sphere.js';
import { ViewOptions } from 'ol/View';
import proj4 from 'proj4';
import env from '../../../../config/env';


const ENV: EnvType = env() as EnvType;
 
proj4.defs(
  "EPSG:5179",
  "+proj=tmerc +lat_0=38 +lon_0=127.5 +k=0.9996 +x_0=1000000 +y_0=2000000 +ellps=GRS80 +units=m +no_defs"
);
 
register(proj4);
const p = get("EPSG:5179")!;
p.setExtent([-200000.0, -28024123.62, 31824123.62, 4000000.0]);

const key = `${ENV.VITE_FOREST_TYPE_IDMD_PATH}`;
const domain = `${ENV.VITE_SLC_ARC_GIS_BASE_URL}`;
// const key = process.env.REACT_APP_VITE_FOREST_TYPE_IDMD_PATH;
// const domain = process.env.REACT_APP_VITE_SLC_ARC_GIS_BASE_URL;

const styles = {
  border: {
    MultiPolygon: new Style({
      stroke: new Stroke({
        width: 2,
        color: [255, 0, 0, 0.5],
      }),
    }),
  },
  aiFeature: {
    // ai 분석결과
    MultiPolygon: new Style({
      stroke: new Stroke({
        width: 4,
        color: [0, 0, 255, 0.8],
      }),
    }),
  },
  outlineStroke: new Style({
    stroke: new Stroke({
      color: [25, 25, 255, 1],
      width: 5,
    }),
  }),
};

const style = new Style({
  fill: new Fill({
    color: 'rgba(255, 255, 255, 0.2)',
  }),
  stroke: new Stroke({
    color: 'rgba(0, 0, 0, 0.5)',
    lineDash: [10, 10],
    width: 2,
  }),
  image: new CircleStyle({
    radius: 5,
    stroke: new Stroke({
      color: 'rgba(0, 0, 0, 0.7)',
    }),
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.2)',
    }),
  }),
});

interface IJGAMap {
  init(): void;
  getCenterCoord(): Coordinate;
  mapRef: React.MutableRefObject<OlMap>;
  setZoomIn(): void;
  setZoomOut(): void;
  getView(): View;
  getJgaLayer(tilename: string): BaseLayer;
  addLayerWithJga(layer: BaseLayer): void;
  getAllLayers(): Array<Layer>; // Get all layers from all layer groups.
  getLayers(): Collection<BaseLayer>; // Get the collection of layers associated with this map.
  getOverlays(id:string): Overlay;
  setMapType(tilename: string, disabled: boolean): void;

  /* 
    - 거리재기, 면적재기 함수들......
    createHelpTooltip(map: any)
    createMeasureTooltip(map:any)
    addInteraction(value: string)
    formatLength(line: any)
    turnLengthMeasure(turn: boolean) 
    turnAreaMeasure(turn: boolean)
  */
}

export type JgaOptions = {
  mapRef: Readonly<React.MutableRefObject<OlMap>>;
  domId: Readonly<string>;
  setMap?: Readonly<SetterOrUpdater<any>>;
  view?: ViewOptions;
  zoom?: number;
  minZoom?: number;
};

const LAYER_Z_INDEX = new Map<'border'| 'cadastral', number>();
LAYER_Z_INDEX.set('border', 50);
LAYER_Z_INDEX.set('cadastral', 9);
export class JGAMap implements IJGAMap {
  mapRef: React.MutableRefObject<OlMap>;
  jejuToken: string;
  targetId: string;
  center: number[];

  /* 
    jgaLayers 기본적으로 map collection도 layer 정보를 갖고 있지만
    map구조체를 통하여 관리하려는 의도.
  */
  protected jgaLayers = new Map<string, any>();
  private view: View;
  private viewPadding = [150, 250, 150, 50];
  private options: JgaOptions;
  private listener: EventsKey;
  private draw: Draw;
  private helpTooltipElement: HTMLDivElement;
  private measureTooltipElement: HTMLDivElement;
  private measureTooltip: Overlay;
  private helpTooltip: Overlay;
  private bottomLeftElement: HTMLDivElement;

  private info: TCoord_system;

  constructor(options: JgaOptions) {
    if (!options.domId) {
      throw new Error('target domId undefined');
    }
    this.mapRef = options.mapRef;
    this.targetId = options.domId;
    this.options = options;
  }

  private setProjection(epsg_code?: string) {
    const find = MapConstant.coord_system.filter((i: { type: string; }) => i.type === epsg_code)[0];
    this.info = find;
    // console.log('find : ', find);

    proj4.defs(epsg_code, find.wkt);
    register(proj4);
  }

  
  init() {
    this.setProjection(this.info?.type || 'EPSG:5179');
    const projection = getProjection('EPSG:5179')!;
  
    const baseSource = new XYZ({
      tileUrlFunction: (coordinate) => {
        return ngiiTileUrlFunction("korean_map", coordinate);
      },
      tileLoadFunction: ngiiTileLoadFunction,
      projection,
      maxResolution: 2088.96,
      tileSize: toSize([256, 256]),
    }); 
    const sateSource =  new XYZ({
      tileUrlFunction: (coordinate) => {
        return ngiiTileUrlFunction("satellite_map", coordinate);
      },
      tileLoadFunction: ngiiTileLoadFunction,
      projection,
      maxResolution: 2088.96,
      tileSize: toSize([256, 256]),
    });

    const satelliteLayer = new TileLayer({
      source: sateSource,
      visible: false,
      properties: { tilename: 'satellite' },
    });
    const baseLayer = new TileLayer({
      source: baseSource,
      properties: { tilename: 'base' },
    });

    const borderLayer = new VectorImageLayer({
      // 지역 경계선
      source: new VectorSource(),
      properties: {
        tilename: 'border',
      },
      zIndex: LAYER_Z_INDEX.get('border'),
      style: styles.border.MultiPolygon,
    });
   
    const overlay = new Overlay({
        positioning: 'center-center',
        element: document.getElementById('overlay')!,
        stopEvent: false,
        id: 'overlay',
    });

    // openlayer's default : EPSG:3857
    this.view = new View({
      projection: getProjection(this.info.type),
      center: this.info.center,
      // extent: this.info.extent,
      constrainResolution: true,
      zoom: 13,
      minZoom: 7,
      maxZoom: 20,
      ...this.options.view,
    });

    this.mapRef.current = new OlMap({
      target: this.targetId,
      view: this.view,
      controls: defaultControls({ zoom: false }),
    });

    // 레이어 등록
    this.addOverLayWithJga(overlay);
    this.addLayerWithJga(satelliteLayer);
    this.addLayerWithJga(baseLayer);
    this.addLayerWithJga(borderLayer);

    this.createHelpTooltip(this.mapRef.current);
    this.createMeasureTooltip(this.mapRef.current);
    this.addBottomLeftText();

    this.mapRef.current.on('loadstart', () => {
      //   this.setMap((prev: TStateMap) => ({ ...prev, status: { ...prev.status, loadStart: true } }));
      // console.log('loadstart');
    });
    this.mapRef.current.on('loadend', () => {
      //   this.setMap((prev: TStateMap) => ({ ...prev, status: { ...prev.status, loadStart: false } }));
      // console.log('load end');
    });
    this.mapRef.current.once('rendercomplete', () => {
      // this.setMap((prev: TStateMap) => ({ ...prev, status: { ...prev.status, loadStart: false } }));
    });
    this.mapRef.current.on('moveend', (evt: any) => {});
    // this.mapRef.current.on('pointermove', this.pointerMoveHandler.bind(this));
  }

  private addBottomLeftText() {
    this.bottomLeftElement = document.createElement('div');
    this.bottomLeftElement.className = 'bottom-left-text';
    this.bottomLeftElement.textContent =
      '※ 주의: 본 자료는 실제 현황과 차이가 있을 수 있으므로 충분히 유의하여 참고용으로만 사용하시고 타 용도로의 사용을 금합니다.';

    const mapElement = this.mapRef.current.getTargetElement();
    mapElement.appendChild(this.bottomLeftElement);
  }

  protected createHelpTooltip(map: any) {
    if (this.helpTooltipElement) {
      this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement);
    }
    this.helpTooltipElement = document.createElement('div');
    this.helpTooltipElement.className = 'ol-tooltip hidden';
    this.helpTooltip = new Overlay({
      element: this.helpTooltipElement,
      offset: [15, 0],
      positioning: 'center-left',
    });
    map.addOverlay(this.helpTooltip);
    return this.helpTooltipElement;
  }

  protected createMeasureTooltip(map: any) {
    if (this.measureTooltipElement) {
      this.measureTooltipElement.parentNode.removeChild(this.measureTooltipElement);
    }
    this.measureTooltipElement = document.createElement('div');
    this.measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
    this.measureTooltip = new Overlay({
      element: this.measureTooltipElement,
      offset: [0, -15],
      positioning: 'bottom-center',
      stopEvent: false,
      insertFirst: false,
    });
    map.addOverlay(this.measureTooltip);
  }

  protected addInteraction(value: string) {
    const type = value === 'area' ? 'Polygon' : 'LineString';
    // const type = 'LineString';
    this.draw = new Draw({
      source: new VectorSource(),
      type: type,
      style: function (feature) {
        const geometryType = feature.getGeometry().getType();
        if (geometryType === type || geometryType === 'Point') {
          return style;
        }
      },
    });
    this.mapRef.current.addInteraction(this.draw);

    this.draw.on('drawstart', (evt: any) => {
      // set sketch
      const sketch: Feature = evt.feature;

      let tooltipCoord = evt.coordinate;

      sketch.getGeometry().on('change', (evt: any) => {
        const geom = evt.target;

        const output = this.formatLength(geom);
        tooltipCoord = geom.getLastCoordinate();

        this.measureTooltipElement.innerHTML = output;
        this.measureTooltip.setPosition(tooltipCoord);
      });
    });
  }

  formatLength(line: any) {
    const length = getLength(line);
    let output;
    if (length > 100) {
      output = `${Math.round((length / 1000) * 100) / 100} km`;
    } else {
      output = `${Math.round(length * 100) / 100} m`;
    }
    return output;
  }

  turnLengthMeasure(turn: boolean) {
    if (turn) {
      this.addInteraction('length');
      this.createHelpTooltip(this.mapRef.current);
      this.createMeasureTooltip(this.mapRef.current);
    } else {
      this.getMap().removeInteraction(this.draw);
      this.getMap().removeOverlay(this.measureTooltip);
      this.getMap().removeOverlay(this.helpTooltip);
    }
  }

  turnAreaMeasure(turn: boolean) {
    if (turn) {
      this.addInteraction('area');
      this.createHelpTooltip(this.mapRef.current);
      this.createMeasureTooltip(this.mapRef.current);
    } else {
      this.getMap().removeInteraction(this.draw);
      this.getMap().removeOverlay(this.measureTooltip);
      this.getMap().removeOverlay(this.helpTooltip);
    }
  }

  moveTo({ location }: { location: number[] }): void {
    const view = this.getView();
    view.setCenter(location);
  }

  flyTo({ location }: { location: number[] }): void {
    const duration = 1000;
    const view = this.getView();
    const zoom = view.getZoom();

    view.animate({
      center: location,
      duration,
    });
    view.animate(
      {
        zoom: zoom - 1,
        duration: duration / 2,
      },
      {
        zoom: 18,
        duration: duration / 2,
      },
    );
  }

  flyToFast({ location, zoom = 19 }: { location: number[]; zoom?: number }): void {
    const view = this.getView();

    view.animate({
      center: location,
      zoom: zoom,
    });
  }

  setMapType(tilename: string, disabled: boolean) {
    if (tilename === 'satellite') {
      const base = this.getJgaLayer('base');
      base.setVisible(false);
    } else if (tilename === 'base') {
      const sate = this.getJgaLayer('satellite');
      sate.setVisible(false);
    }
    const layer = this.getJgaLayer(tilename);
    const layerPoly = this.getJgaLayer(`${tilename}_poly`);
    layer.setVisible(disabled);
    if (layerPoly) {
      // 같은 좌표 데이터를 이용하여 그리는데 특정 레벨에서는 클러스터, 특정 레벨에서는 폴리곤이여야하므로
      // 클러스터와 똑같은 레이어를 더 만들어 _poly라고 붙임.
      layerPoly.setVisible(disabled);
    }
  }


  setZoomIn() {
    this.mapRef.current.getView().animate({
      zoom: this.mapRef.current.getView().getZoom() + 1,
      duration: 250,
    });
  }

  setZoomOut() {
    this.mapRef.current.getView().animate({
      zoom: this.mapRef.current.getView().getZoom() - 1,
      duration: 250,
    });
  }

  getCenterCoord(): Coordinate {
    const center = this.mapRef.current.getView().getCenter();

    return center;
  }

  addLayerWithJga(layer: BaseLayer) {
    this.setJgaLayer(layer);
    this.mapRef.current.addLayer(layer);
  }

  addOverLayWithJga(layer: Overlay) {
    // this.setJgaLayer(layer);
    this.mapRef.current.addOverlay(layer);
  }

  addTileLayer(layer: any) {
    this.jgaLayers.set('uav', layer);
    this.mapRef.current.addLayer(layer);
  }

  private setJgaLayer(layer: any): void {
    if (!layer) throw new Error('layer is undefined');
    this.jgaLayers.set(layer.getProperties().tilename, layer);
  }

  removeLayer(tilename: string) {
    try {
      const layer = this.getJgaLayer(tilename);
      this.mapRef.current.removeLayer(layer);
    } catch (e) {
      console.log(e);
    }
  }

  addToLayerGroup(layerGroup: LayerGroup) {
    if (layerGroup instanceof LayerGroup) {
      if (!layerGroup) throw new Error('layerGroup is undefined');
      this.jgaLayers.set(layerGroup.getProperties().tilename, layerGroup);
      layerGroup.getLayers().forEach((layer) => {
        this.jgaLayers.set(layer.getProperties().tilename, layer);
      });
      this.addLayerWithJga(layerGroup);
    }
  }

  getJgaLayer(name: string): BaseLayer {
    return this.jgaLayers.get(name);
  }

  getAllLayers(): Array<Layer> {
    return this.mapRef.current.getAllLayers();
  }

  getLayers(): Collection<BaseLayer> {
    // Get the collection of layers associated with this map.
    return this.mapRef.current.getLayers();
  }

  getOverlays(id:string): Overlay {
    // console.log(this.mapRef.current.getOverlayById(id));
    return this.mapRef.current.getOverlayById(id);
  }

  getView(): View {
    return this.mapRef.current.getView();
  }

  getMap() {
    return this.mapRef.current;
  }

  // 시군구 경계 등
  drawBorder(geojson: GeoJSONFeature) {
    // const property = geojson.features[0].properties;
    const layer = this.jgaLayers.get('border');
    const borderSource = layer.getSource();
    borderSource.clear();

    const parse = JSON.parse(geojson);
    const feature = new GeoJSON().readFeatures(parse);
    const geo = feature[0].getGeometry();
    this.view.fit(geo as SimpleGeometry, { padding: this.viewPadding });
    borderSource.addFeatures(feature);
  }

  clearBorderFeature() {
    const layer = this.jgaLayers.get('border') as VectorLayer<VectorSource>;
    const borderSource = layer.getSource();
    borderSource.clear();
  }
}
